/**
 * Token Burn Library
 * Portable modules for SPL token burning functionality
 *
 * Usage:
 * ```typescript
 * import { executeBurn, fetchTokenAccounts, formatTokenAmount } from "@/lib/burn"
 * ```
 */

// Burn transaction logic
export {
  executeBurn,
  buildBurnTransaction,
  checkSignatureStatus,
  waitForConfirmation,
  closeEmptyAccountsBatch,
  buildCloseAccountsTransaction,
  fetchWithheldFeeInfo,
  burnNFTBatch,
  burnNFTs,
  FORGEPAD_FEE_PERCENT,
  FORGEPAD_FEE_WALLET,
  TYPICAL_RENT_PER_ACCOUNT,
  TYPICAL_RENT_LAMPORTS,
  TYPICAL_NFT_RENT,
  TYPICAL_NFT_RENT_LAMPORTS,
  MAX_NFTS_PER_TX,
  type BurnResult,
  type BurnStatus,
  type BurnStatusCallback,
  type CloseEmptyAccountsResult,
  type BuildCloseAccountsTxResult,
  type BurnNFTResult,
  type CloseAccountInput,
  type WithheldFeeInfo,
} from "./burn";

// Token account fetching
export {
  fetchTokenAccounts,
  fetchEmptyTokenAccounts,
  getSolBalance,
  hasSufficientSol,
  verifyTokenProgram,
  type TokenAccountInfo,
  type EmptyTokenAccount,
} from "./tokens";

// NFT fetching
export { fetchNFTs, type NFTInfo } from "./nfts";

// Token metadata
export {
  getTokenMetadataWithFallback,
  enrichTokensWithMetadata,
  fetchMetaplexMetadata,
  getTokenDisplayName,
  clearMetadataCache,
  type TokenMetadata,
} from "./metadata";

// Formatting utilities
export {
  formatTokenAmount,
  formatCompactBalance,
  parseTokenAmount,
  formatWithCommas,
  shortenAddress,
  formatSol,
  estimateTransactionFee,
} from "./format";

// Error handling
export { getUserFriendlyError, mightHaveSucceeded } from "./errors";

// Burn history
export {
  getBurns,
  addBurn,
  clearBurns,
  formatTimeAgo,
  getBurnCount,
  createBurnRecord,
  type BurnRecord,
} from "./burnHistory";
